import { useEffect, useRef, type RefObject } from "react";

export type DismissReason = "escape" | "outside";

export function useDismissable<T extends HTMLElement>(
  open: boolean,
  onDismiss: (reason: DismissReason) => void,
  alsoInside?: RefObject<HTMLElement | null>,
) {
  const ref = useRef<T>(null);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node;

      if (
        ref.current?.contains(target) ||
        alsoInside?.current?.contains(target)
      ) {
        return;
      }

      onDismissRef.current("outside");
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onDismissRef.current("escape");
      }
    }

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open, alsoInside]);

  return ref;
}
